import React from 'react';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { ArrowRightIcon, Button, ProfileIcon, VerifiedIcon } from '@motorway/mw-highway-code';
import { InfoBox } from '@motorway/mw-highway-code/alpha';
import { INFOBOX_VARIANT, SIZE, VARIANT } from '@motorway/mw-highway-code/enums';

import { Modal } from 'Storybook/components/local/Modal/Modal';

import { PAYMENT_PAGE_EVENTS } from '../../../../../utilities/analytics/analyticsPaymentPage';
import { formatPriceTransactional } from '../../../../../utilities/formatPrice';
import { MASK_CHAR } from '../../../documentCapture/addBankDetails/AddBankDetails.helpers';
import { ACCOUNT_NUMBER_MASKED_FORMAT } from '../../../documentCapture/addBankDetails/fields/AccountNumber';
import PaymentsTexts from '../../../payments/Payments.text.json';
import { useSaleDetailsSub } from '../../../vehicleCollection/hooks/useSaleDetailsSub';
import { useVehicleCollectionSub } from '../../../vehicleCollection/hooks/useVehicleCollectionSub';
import { validateHasTransactionalValue } from '../../../vehicleCollection/utils/validator';

import dealershipStyles from '../../../documentCapture/DealerDetails/DealershipDetailsModal/DealershipDetailsModal.scss';
import styles from './ConfirmNewOfferModal.module.scss';

const LocalT = new MDText(PaymentsTexts.payments.enterView);

type ConfirmNewOfferModalProps = {
  className?: cx.Argument;
  disabled?: boolean;
  onConfirm: () => void;
  onReject: () => void;
}

export const ConfirmNewOfferModal = ({
  className,
  disabled = false,
  onConfirm,
  onReject,
}: ConfirmNewOfferModalProps) => {
  const {
    accountNumber,
    dealershipName,
    financingAmount,
    hasNegativeEquity,
    sellerAmount,
    soldForFigure,
  } = useSaleDetailsSub();

  const { vrm } = useVehicleCollectionSub();

  // store `accountNumber` is only last 4!!!
  const maskedAccountNumber = accountNumber
    ? accountNumber.padStart(ACCOUNT_NUMBER_MASKED_FORMAT.length, MASK_CHAR)
    : null;

  const handleModalClose = () => {
    PAYMENT_PAGE_EVENTS.VEHICLE_PAYMENT_CONFIRM_NEW_OFFER_MODAL_CLOSED(vrm);
    onReject();
  };

  return (
    <Modal
      centered={false}
      className={cx(styles.confirmNewOfferModal, dealershipStyles.container)}
      contentClass={cx(styles.modalContent, className)}
      dialogClass={dealershipStyles.modal}
      dismissModal={handleModalClose}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
      size={SIZE.SMALL}
    >
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <div className={styles.header}>
            <h3 className={styles.title}>{LocalT.translate('confirmSaleModal.title')}</h3>
            <p className={styles.subtitle}>{LocalT.translate(`confirmSaleModal.${hasNegativeEquity ? 'negativeEquitySubtitle' : 'positiveEquitySubtitle'}`)}</p>
          </div>
          <div className={styles.tranferDetails}>
            <h2 className={styles.sellerFigure}>
              {formatPriceTransactional(hasNegativeEquity ? (soldForFigure ?? 0) : (sellerAmount ?? 0))}
            </h2>
            { !hasNegativeEquity ? (
              <>
                <span className={styles.sellerFigureSubtitle}>{LocalT.translate('confirmSaleModal.viaBankTransfer')}</span>
                <hr className={styles.separator} />
                <div className={styles.transactionContainer}>
                  <div className={styles.iconWrapper}>
                    <div className={cx(styles.transactionIcon, styles.verifiedIcon)}>
                      <VerifiedIcon size="medium"/>
                    </div>
                    <div className={cx(styles.transactionText, styles.ellipsis)}>{dealershipName}</div>
                  </div>
                  <span className={styles.chevron}><ArrowRightIcon size="xsmall" /></span>
                  <div className={styles.iconWrapper}>
                    <div className={cx(styles.transactionIcon, styles.profileIcon)}>
                      <ProfileIcon filled size="medium"/>
                    </div>
                    <div className={styles.transactionText}>{ maskedAccountNumber || LocalT.translate('confirmSaleModal.yourAccount')}</div>
                  </div>
                </div>
              </>
            ) : null }
          </div>
          {validateHasTransactionalValue(financingAmount) ? (
            <div className={styles.financeInfoBox}>
              <InfoBox
                content={LocalT.translate('confirmSaleModal.settlingFinanceContent', { amount: formatPriceTransactional(financingAmount) })}
                heading={LocalT.translate('confirmSaleModal.settlingFinanceHeading')}
                variant={INFOBOX_VARIANT.INFO}
              />
            </div>
          ) : null }
        </div>
        <div className={styles.actions}>
          <Button
            fullWidth
            className={styles.confirmButton}
            disabled={disabled}
            icon="chevron"
            label={LocalT.translate('confirmSaleModal.acceptOffer')}
            onClick={onConfirm}
            size={SIZE.MEDIUM}
            variant={VARIANT.PRIMARY}
          />
        </div>
      </div>
    </Modal>
  );
};
